import { useFormikContext } from "formik";
import React, { FC, useEffect, useMemo } from "react";

import {
  CreateClubWizardSteps,
  GovernanceConfigurationType,
} from "../../../common/enums/clubs-wizard.enum";
import { ICreateClubWizardForm } from "../../../common/interfaces/clubs-wizard.interface";
import CustomCheckbox from "../../CustomCheckbox/CustomCheckbox";
import Fees from "../../Fees/Fees";

import BasicInfoPreview from "./BasicInfoPreview";
import "./ClubPreview.scss";
import PreviewComponent from "./PreviewComponent";
import {
  ADD_MEMBER_FEE,
  CLUB_CREATING_FEE,
  CREATE_FUNDRAISE_FEE,
} from "../../../common/constants/fees";
import {
  DISCLAIMERS_PDF_URL,
  EMPTY_STRING,
  PRIVACY_POLICY_PDF_URL,
  SOL_SYMBOL,
  TERMS_OF_USE_PDF_URL,
  TOTAL_FEES,
} from "../../../common/constants/common.constants";
import CollectionData from "../NftConfiguration/CollectionData/CollectionData";
import { IExpandedPreviewData } from "../../../common/interfaces/common.interface";
import { clubTypeSteps } from "../../../common/constants/clubs-wizard.constants";
import VotingConfiguration from "../../VotingConfiguration/VotingConfiguration";
import { RestrictionOptionsMap } from "../../../common/constants/civic.constants";
import { RestrictionOptions } from "../../../common/enums/civic.enum";

interface ClubPreviewProps {
  jumpToSection: (sectionStep: CreateClubWizardSteps) => void;
}

const ClubPreview: FC<ClubPreviewProps> = ({ jumpToSection }) => {
  const { values, errors, setFieldValue, setErrors } =
    useFormikContext<ICreateClubWizardForm>();

  useEffect(() => {
    const totalFees = calculateFees();
    setFieldValue(TOTAL_FEES, totalFees);
  }, [errors]);

  const allSteps = useMemo(() => {
    return clubTypeSteps.get(values.governance);
  }, [values]);

  const modifySection = (step: CreateClubWizardSteps) => {
    setErrors({});

    const jumpTo =
      allSteps?.find((item) => item === step) ||
      CreateClubWizardSteps.BasicInfo;

    jumpToSection(jumpTo);
  };

  const getGovernanceConfigDescription = () => {
    switch (values.governance) {
      case GovernanceConfigurationType.NFT:
        return "Nft-based";
      case GovernanceConfigurationType.RoleBased:
        return "Role-based";
      case GovernanceConfigurationType.ExistingToken:
        return "Deposit-based";
      case GovernanceConfigurationType.ProjectFundraise:
        return "Project fundraise";
      default:
        return EMPTY_STRING;
    }
  };

  const calculateFees = useMemo(() => {
    return () => {
      let totalFees =
        CLUB_CREATING_FEE + (values.initialMembers.length - 1) * ADD_MEMBER_FEE;
      totalFees += !values.skipFundraise ? CREATE_FUNDRAISE_FEE : 0;
      return totalFees;
    };
  }, []);

  const rolePreview = useMemo(() => {
    const roles: IExpandedPreviewData[] = [
      {
        label: "Founder",
        amount:
          values.ownerVotingPower &&
          values.governance === GovernanceConfigurationType.RoleBased
            ? `(${values.ownerVotingPower} Votes)`
            : "",
      },
    ];

    values.member &&
      roles.push({
        label: "Member",
        amount:
          values.memberVotingPower &&
          values.governance === GovernanceConfigurationType.RoleBased
            ? `(${values.memberVotingPower} Votes)`
            : "",
      });

    values.investor &&
      roles.push({
        label: "Investor",
        amount: "",
      });

    values.curator &&
      roles.push({
        label: "Manager",
        amount:
          values.curator &&
          values.governance === GovernanceConfigurationType.RoleBased
            ? `(${values.curatorVotingPower} Votes)`
            : "",
      });

    return roles;
  }, [values]);

  const shouldEditComponent = (step: CreateClubWizardSteps) =>
    !!allSteps?.find((item) => item === step);

  return (
    <div className="club-preview">
      <BasicInfoPreview
        changeStepHandler={() => modifySection(CreateClubWizardSteps.BasicInfo)}
      />
      <PreviewComponent
        label="Governance configuration"
        showEditIcon={shouldEditComponent(
          CreateClubWizardSteps.GovernanceConfiguration
        )}
        previewContent={[
          {
            description: getGovernanceConfigDescription(),
          },
        ]}
        editSection={() =>
          modifySection(CreateClubWizardSteps.GovernanceConfiguration)
        }
      />
      {values.governance === GovernanceConfigurationType.NFT && (
        <div className="club-preview__collection-info">
          <p>Collection info</p>
          <CollectionData collectionData={values.collectionData} />
        </div>
      )}
      <div className="club-preview__role-members">
        <PreviewComponent
          showEditIcon={shouldEditComponent(CreateClubWizardSteps.Roles)}
          label="Role configuration"
          editSection={() => modifySection(CreateClubWizardSteps.Roles)}
          previewContent={[
            {
              expandedDescription: rolePreview,
            },
          ]}
        />
        <PreviewComponent
          showEditIcon={shouldEditComponent(CreateClubWizardSteps.Members)}
          editSection={() => modifySection(CreateClubWizardSteps.Members)}
          label={`${
            values.governance === GovernanceConfigurationType.ProjectFundraise
              ? "Participants"
              : "Club members"
          }`}
          previewContent={[
            {
              description: values.initialMembers.length,
              additionalDescription: "out of 99",
            },
          ]}
        />
        {values.eligibleUsers === RestrictionOptions.VerifiedUsers && (
          <div className="club-preview__verification-badges">
            <p className="club-preview__verification-description">
              Membership restrictions
            </p>
            <div className="club-preview__verification-badge-wrapper">
              <div className="club-preview__verification-badge-wrapper__badge">
                Verified
              </div>
              <div className="club-preview__verification-badge-wrapper__badge">
                {RestrictionOptionsMap[values.eligibleLocation].label}
              </div>
            </div>
          </div>
        )}
      </div>
      <PreviewComponent
        showEditIcon={shouldEditComponent(
          CreateClubWizardSteps.FundraisingConfiguration
        )}
        editSection={() =>
          modifySection(CreateClubWizardSteps.FundraisingConfiguration)
        }
        label="Main currency"
        previewContent={[{ description: values.mintSymbol }]}
      />
      <VotingConfiguration
        showEditVoting={shouldEditComponent(
          CreateClubWizardSteps.FundraisingConfiguration
        )}
        defaultMaxVotingTime={values.maxVoteTime}
        defaultVotingQuorum={values.votingQuorum}
        transferMaxVotingTime={values.transferMaxVoteTime}
        isClubCreation
        modifySection={modifySection}
        mintDecimals={values.mintDecimals}
        mintSymbol={values.mintSymbol ?? SOL_SYMBOL}
        tradeRanges={
          values.governance !== GovernanceConfigurationType.ProjectFundraise
        }
        sellPermissionConfig={
          values.votingThreshold.length > 0
            ? {
                divideByLamports: false,
                permissionsAmount: values.votingThreshold.length,
                permissions: values.votingThreshold.map((threshold) => {
                  return {
                    from: threshold.from,
                    to: threshold.to,
                    votingQuorum: threshold.approvalQuorum,
                  };
                }),
              }
            : undefined
        }
        transferVotingQuorum={values.transferVotingQuorum}
        withdrawalMaxVotingTime={values.withdrawalMaxVoteTime}
        withdrawalVotingQuorum={values.withdrawalVotingQuorum}
        clubConfigMaxVotingTime={values.clubConfigMaxVoteTime}
        clubConfigQuorum={values.clubConfigQuorum}
      />
      <div className="club-preview__terms-conditions">
        <CustomCheckbox
          checked={values.termsConditions}
          onClickMethod={() => {
            setFieldValue("termsConditions", !values.termsConditions);
          }}
        />
        <p
          className={
            errors.termsConditions
              ? "club-preview__terms-text-error"
              : "club-preview__terms-text"
          }
        >
          I have read and accept{" "}
          <a
            href={TERMS_OF_USE_PDF_URL}
            target={"_blank"}
            rel="noreferrer"
            className={
              errors.termsConditions
                ? "club-preview__link-error"
                : "club-preview__link"
            }
          >
            Terms and Conditions
          </a>{" "}
          as well as{" "}
          <a
            href={DISCLAIMERS_PDF_URL}
            target={"_blank"}
            rel="noreferrer"
            className={
              errors.termsConditions
                ? "club-preview__link-error"
                : "club-preview__link"
            }
          >
            Data use{" "}
          </a>
          and{" "}
          <a
            href={PRIVACY_POLICY_PDF_URL}
            target={"_blank"}
            rel="noreferrer"
            className={
              errors.termsConditions
                ? "club-preview__link-error"
                : "club-preview__link"
            }
          >
            Cookies policy
          </a>
        </p>
      </div>
      <Fees
        feesTitle="Solana network fee for this action:"
        feesDescription={`${values.totalFees.toFixed(4)} SOL`}
      />
    </div>
  );
};

export default ClubPreview;
